import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Plus, AlertTriangle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useEligiblePosts,
  useCreateCommentAutomation,
  useUpdateCommentAutomation,
  useAutomationWorkflows,
  WORKFLOW_PAYLOAD_PREFIX,
  type AutomationButton,
  type CommentAutomation,
  type MatchMode,
} from "@/hooks/use-comment-automations";

// Zernio caps the DM at ~640 chars once buttons are attached, ~1000 otherwise.
const DM_MAX_WITH_BUTTONS = 640;
const DM_MAX_PLAIN = 1000;
const MAX_KEYWORDS = 20;
const MAX_BUTTONS = 3;

interface AutomationDraft {
  name: string;
  platform: "INSTAGRAM" | "FACEBOOK";
  trigger: "comment" | "story_reply";
  scopeIsPost: boolean;
  scheduledPostId: string | null;
  keywords: string[];
  matchMode: MatchMode;
  excludeKeywords: string[];
  typoTolerance: boolean;
  alsoMatchInDms: boolean;
  /** Instagram only: gate the DM behind a tap on a button. */
  tapToUnlock: boolean;
  gateMessage: string;
  gateButtonLabel: string;
  dmMessage: string;
  commentReply: string;
  buttons: AutomationButton[];
  linkTracking: boolean;
  clickTag: string;
}

function isWorkflowButton(b: AutomationButton): boolean {
  return b.type === "postback" && (b.payload ?? "").startsWith(WORKFLOW_PAYLOAD_PREFIX);
}

function emptyDraft(): AutomationDraft {
  return {
    name: "",
    platform: "INSTAGRAM",
    trigger: "comment",
    scopeIsPost: false,
    scheduledPostId: null,
    keywords: [],
    // Whole-word by default: `app` should not fire on `happy`.
    matchMode: "word",
    excludeKeywords: [],
    typoTolerance: false,
    alsoMatchInDms: false,
    tapToUnlock: false,
    gateMessage: "",
    gateButtonLabel: "",
    dmMessage: "",
    commentReply: "",
    buttons: [],
    linkTracking: true,
    clickTag: "",
  };
}

function draftFromAutomation(a: CommentAutomation): AutomationDraft {
  return {
    name: a.name ?? "",
    platform: (a.platform?.toUpperCase() as "INSTAGRAM" | "FACEBOOK") ?? "INSTAGRAM",
    trigger: a.trigger ?? "comment",
    scopeIsPost: Boolean(a.platformPostId),
    scheduledPostId: null,
    keywords: a.keywords ?? [],
    matchMode: a.matchMode ?? "contains",
    excludeKeywords: a.excludeKeywords ?? [],
    typoTolerance: Boolean(a.typoTolerance),
    alsoMatchInDms: Boolean(a.alsoMatchInDms),
    tapToUnlock: Boolean(a.audience?.tapToUnlock),
    gateMessage: a.followGate?.message ?? "",
    gateButtonLabel: a.followGate?.buttonLabel ?? "",
    dmMessage: a.dmMessage ?? "",
    commentReply: a.commentReply ?? "",
    buttons: a.buttons ?? [],
    linkTracking: a.linkTracking ?? true,
    clickTag: a.clickTag ?? "",
  };
}

export interface AutomationEditorSheetProps {
  brandId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present = edit mode. Zernio's update body cannot change platform, trigger
   *  or scope, so those render read-only. */
  automation?: CommentAutomation | null;
}

export function AutomationEditorSheet({
  brandId,
  open,
  onOpenChange,
  automation,
}: AutomationEditorSheetProps) {
  const { t } = useTranslation("inbox");
  const isEdit = Boolean(automation);
  const [draft, setDraft] = useState<AutomationDraft>(emptyDraft);
  const [keywordInput, setKeywordInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");

  const create = useCreateCommentAutomation(brandId);
  const update = useUpdateCommentAutomation(brandId);
  const saving = create.isPending || update.isPending;

  const posts = useEligiblePosts(brandId, open && !isEdit && draft.scopeIsPost);
  const hasPostback = draft.buttons.some((b) => b.type === "postback");
  const workflows = useAutomationWorkflows(brandId, open && hasPostback);
  const isInstagram = draft.platform === "INSTAGRAM";

  useEffect(() => {
    if (!open) return;
    setDraft(automation ? draftFromAutomation(automation) : emptyDraft());
    setKeywordInput("");
    setExcludeInput("");
  }, [open, automation]);

  const dmMax = draft.buttons.length > 0 ? DM_MAX_WITH_BUTTONS : DM_MAX_PLAIN;

  function set<K extends keyof AutomationDraft>(key: K, value: AutomationDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function addTo(key: "keywords" | "excludeKeywords", input: string, clear: () => void) {
    const raw = input.trim();
    const list = draft[key];
    if (!raw || list.length >= MAX_KEYWORDS) return;
    if (!list.includes(raw)) set(key, [...list, raw]);
    clear();
  }

  function addKeyword() {
    addTo("keywords", keywordInput, () => setKeywordInput(""));
  }

  /** Mirrors the router's zod rules so the user sees the problem before a round trip. */
  function validateLocal(): string | null {
    if (!draft.name.trim()) return t("automations.validation.nameRequired");
    if (!draft.dmMessage.trim()) return t("automations.validation.dmRequired");
    // Reachable: adding a button drops dmMax from 1000 to 640, and maxLength
    // does not truncate text already in the textarea.
    if (draft.dmMessage.length > dmMax) {
      return t("automations.validation.dmTooLong");
    }
    if (!isEdit && draft.scopeIsPost && !draft.scheduledPostId) {
      return t("automations.validation.postRequired");
    }
    if (
      draft.alsoMatchInDms &&
      draft.trigger === "comment" &&
      draft.keywords.length === 0
    ) {
      return t("automations.validation.dmMatchNeedsKeyword");
    }
    for (const b of draft.buttons) {
      if (!b.title.trim()) return t("automations.validation.buttonTextRequired");
      if (b.type === "url") {
        try {
          new URL(b.url ?? "");
        } catch {
          return t("automations.validation.invalidUrl");
        }
      }
      if (isWorkflowButton(b) && b.payload === WORKFLOW_PAYLOAD_PREFIX) {
        return t("automations.validation.workflowRequired");
      }
      if (b.type === "postback" && !b.payload?.trim()) {
        return t("automations.validation.payloadRequired");
      }
      if (b.type === "phone" && !b.phone?.trim()) {
        return t("automations.validation.phoneRequired");
      }
    }
    return null;
  }

  async function onSubmit() {
    const problem = validateLocal();
    if (problem) {
      toast.error(problem);
      return;
    }
    const isComment = draft.trigger === "comment";
    const shared = {
      name: draft.name.trim(),
      keywords: draft.keywords,
      matchMode: draft.matchMode,
      excludeKeywords: draft.excludeKeywords,
      // Typo tolerance only exists for whole-word matching.
      typoTolerance: draft.matchMode === "word" && draft.typoTolerance,
      ...(isComment ? { alsoMatchInDms: draft.alsoMatchInDms } : {}),
      dmMessage: draft.dmMessage,
      linkTracking: draft.linkTracking,
    };
    // Keep audience/followGate rules set outside Pilot; only tapToUnlock and
    // the gate copy are edited here.
    const followGate = draft.tapToUnlock
      ? {
          ...(automation?.followGate ?? {}),
          ...(draft.gateMessage.trim() ? { message: draft.gateMessage.trim() } : {}),
          ...(draft.gateButtonLabel.trim()
            ? { buttonLabel: draft.gateButtonLabel.trim() }
            : {}),
        }
      : null;
    const audience =
      isInstagram && (draft.tapToUnlock || automation?.audience)
        ? { ...(automation?.audience ?? {}), tapToUnlock: draft.tapToUnlock }
        : null;
    // clickTag is meaningless without linkTracking — the API rejects that pair.
    const clickTag =
      draft.linkTracking && draft.clickTag.trim() ? draft.clickTag.trim() : null;
    const commentReply = draft.commentReply.trim() || null;
    const buttons = draft.buttons.length ? draft.buttons : null;
    try {
      if (isEdit && automation) {
        // Explicit null CLEARS. Omitting the key leaves the stored value in
        // place, which made buttons / reply / tag impossible to remove.
        await update.mutateAsync({
          automationId: automation.id,
          ...shared,
          commentReply,
          buttons,
          clickTag,
          ...(isInstagram
            ? {
                audience,
                followGate: followGate ?? automation?.followGate ?? null,
              }
            : {}),
        });
        toast.success(t("automations.toasts.updated"));
      } else {
        await create.mutateAsync({
          ...shared,
          platform: draft.platform,
          trigger: draft.trigger,
          ...(commentReply ? { commentReply } : {}),
          ...(buttons ? { buttons } : {}),
          ...(clickTag ? { clickTag } : {}),
          ...(isInstagram && draft.tapToUnlock
            ? { audience: { tapToUnlock: true }, followGate: followGate ?? {} }
            : {}),
          ...(draft.scopeIsPost && draft.scheduledPostId
            ? { scheduledPostId: draft.scheduledPostId }
            : {}),
        });
        toast.success(t("automations.toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("automations.toasts.error"),
      );
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-lg w-full flex flex-col gap-0 overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-sm font-semibold">
            {isEdit ? t("automations.actions.edit") : t("automations.new")}
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {t("automations.subtitle")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="ca-name">{t("automations.fields.name")}</Label>
            <Input
              id="ca-name"
              value={draft.name}
              maxLength={80}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          {isEdit ? (
            <p className="text-xs text-muted-foreground">
              {t("automations.fields.scopeLocked")}
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>{t("automations.fields.platform")}</Label>
                <Select
                  value={draft.platform}
                  onValueChange={(v) => set("platform", v as "INSTAGRAM" | "FACEBOOK")}
                >
                  <SelectTrigger aria-label={t("automations.fields.platform")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INSTAGRAM">Instagram</SelectItem>
                    <SelectItem value="FACEBOOK">Facebook</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t("automations.fields.trigger")}</Label>
                <Select
                  value={draft.trigger}
                  onValueChange={(v) => {
                    const trigger = v as "comment" | "story_reply";
                    // Story replies are account-wide by definition.
                    setDraft((d) => ({
                      ...d,
                      trigger,
                      scopeIsPost: trigger === "story_reply" ? false : d.scopeIsPost,
                      scheduledPostId:
                        trigger === "story_reply" ? null : d.scheduledPostId,
                    }));
                  }}
                >
                  <SelectTrigger aria-label={t("automations.fields.trigger")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comment">
                      {t("automations.trigger.comment")}
                    </SelectItem>
                    <SelectItem value="story_reply">
                      {t("automations.trigger.storyReply")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {draft.trigger === "comment" && (
                <div className="space-y-1.5">
                  <Label>{t("automations.fields.post")}</Label>
                  <Select
                    value={draft.scopeIsPost ? "post" : "account"}
                    onValueChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        scopeIsPost: v === "post",
                        scheduledPostId: v === "post" ? d.scheduledPostId : null,
                      }))
                    }
                  >
                    <SelectTrigger aria-label={t("automations.fields.post")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account">
                        {t("automations.scope.accountWide")}
                      </SelectItem>
                      <SelectItem value="post">
                        {t("automations.scope.specificPost")}
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {draft.scopeIsPost && (
                    <Select
                      value={draft.scheduledPostId ?? ""}
                      onValueChange={(v) => set("scheduledPostId", v)}
                    >
                      <SelectTrigger
                        className="mt-2"
                        aria-label={t("automations.fields.postPlaceholder")}
                      >
                        <SelectValue
                          placeholder={t("automations.fields.postPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(posts.data ?? []).length === 0 ? (
                          <div className="px-2 py-3 text-xs text-muted-foreground">
                            {t("automations.fields.noEligiblePosts")}
                          </div>
                        ) : (
                          (posts.data ?? []).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.pieceTitle ?? p.zernioPostId}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ca-keyword">{t("automations.fields.keywords")}</Label>
            <Input
              id="ca-keyword"
              value={keywordInput}
              placeholder={t("automations.fields.keywordsPlaceholder")}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t("automations.fields.keywordsHint")}
            </p>
            {draft.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {draft.keywords.map((k) => (
                  <Badge key={k} variant="secondary" className="gap-1">
                    {k}
                    <button
                      type="button"
                      aria-label={`${t("automations.actions.delete")} ${k}`}
                      onClick={() =>
                        set(
                          "keywords",
                          draft.keywords.filter((x) => x !== k),
                        )
                      }
                    >
                      <X size={11} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t("automations.fields.matchMode")}</Label>
            <Select
              value={draft.matchMode}
              onValueChange={(v) => set("matchMode", v as MatchMode)}
            >
              <SelectTrigger aria-label={t("automations.fields.matchMode")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="word">
                  {t("automations.fields.matchWord")}
                </SelectItem>
                <SelectItem value="contains">
                  {t("automations.fields.matchContains")}
                </SelectItem>
                <SelectItem value="exact">
                  {t("automations.fields.matchExactMessage")}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t(`automations.fields.matchHint.${draft.matchMode}`)}
            </p>
          </div>

          {draft.matchMode === "word" && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="ca-typo">{t("automations.fields.typoTolerance")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("automations.fields.typoToleranceHint")}
                </p>
              </div>
              <Switch
                id="ca-typo"
                checked={draft.typoTolerance}
                onCheckedChange={(v) => set("typoTolerance", v)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ca-exclude">{t("automations.fields.excludeKeywords")}</Label>
            <Input
              id="ca-exclude"
              value={excludeInput}
              placeholder={t("automations.fields.excludeKeywordsPlaceholder")}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTo("excludeKeywords", excludeInput, () => setExcludeInput(""));
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t("automations.fields.excludeKeywordsHint")}
            </p>
            {draft.excludeKeywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {draft.excludeKeywords.map((k) => (
                  <Badge key={k} variant="outline" className="gap-1">
                    {k}
                    <button
                      type="button"
                      aria-label={`${t("automations.actions.delete")} ${k}`}
                      onClick={() =>
                        set(
                          "excludeKeywords",
                          draft.excludeKeywords.filter((x) => x !== k),
                        )
                      }
                    >
                      <X size={11} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {draft.trigger === "comment" && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="ca-dms">{t("automations.fields.alsoMatchInDms")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("automations.fields.alsoMatchInDmsHint")}
                </p>
              </div>
              <Switch
                id="ca-dms"
                checked={draft.alsoMatchInDms}
                onCheckedChange={(v) => set("alsoMatchInDms", v)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ca-dm">{t("automations.fields.dmMessage")}</Label>
            <Textarea
              id="ca-dm"
              rows={4}
              value={draft.dmMessage}
              maxLength={dmMax}
              onChange={(e) => set("dmMessage", e.target.value)}
            />
            <p className="text-xs text-muted-foreground text-right">
              {draft.dmMessage.length}/{dmMax}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ca-reply">{t("automations.fields.commentReply")}</Label>
            <Textarea
              id="ca-reply"
              rows={2}
              value={draft.commentReply}
              maxLength={500}
              onChange={(e) => set("commentReply", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("automations.fields.buttons")}</Label>
              {draft.buttons.length < MAX_BUTTONS && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    set("buttons", [
                      ...draft.buttons,
                      { type: "url", title: "", url: "" },
                    ])
                  }
                >
                  <Plus size={13} className="mr-1" />
                  {t("automations.fields.addButton")}
                </Button>
              )}
            </div>
            {draft.buttons.map((b, i) => (
              <div key={i} className="rounded-md border border-border p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Select
                    value={b.type}
                    onValueChange={(v) => {
                      const next = [...draft.buttons];
                      next[i] = { type: v as AutomationButton["type"], title: b.title };
                      set("buttons", next);
                    }}
                  >
                    <SelectTrigger
                      className="w-32"
                      aria-label={t("automations.fields.buttonType")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="url">URL</SelectItem>
                      <SelectItem value="postback">Postback</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    aria-label={t("automations.fields.buttonText")}
                    placeholder={t("automations.fields.buttonText")}
                    value={b.title}
                    maxLength={20}
                    onChange={(e) => {
                      const next = [...draft.buttons];
                      next[i] = { ...b, title: e.target.value };
                      set("buttons", next);
                    }}
                  />
                  <button
                    type="button"
                    aria-label={t("automations.actions.delete")}
                    onClick={() =>
                      set(
                        "buttons",
                        draft.buttons.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
                {b.type === "url" && (
                  <Input
                    aria-label={t("automations.fields.buttonUrl")}
                    placeholder="https://example.com"
                    value={b.url ?? ""}
                    onChange={(e) => {
                      const next = [...draft.buttons];
                      next[i] = { ...b, url: e.target.value };
                      set("buttons", next);
                    }}
                  />
                )}
                {b.type === "postback" && (
                  <div className="space-y-2">
                    <Select
                      value={isWorkflowButton(b) ? "workflow" : "payload"}
                      onValueChange={(v) => {
                        const next = [...draft.buttons];
                        next[i] = {
                          ...b,
                          payload: v === "workflow" ? WORKFLOW_PAYLOAD_PREFIX : "",
                        };
                        set("buttons", next);
                      }}
                    >
                      <SelectTrigger aria-label={t("automations.fields.postbackAction")}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="workflow">
                          {t("automations.fields.postbackWorkflow")}
                        </SelectItem>
                        <SelectItem value="payload">
                          {t("automations.fields.postbackPayload")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {isWorkflowButton(b) ? (
                      <Select
                        value={(b.payload ?? "").slice(WORKFLOW_PAYLOAD_PREFIX.length)}
                        onValueChange={(id) => {
                          const next = [...draft.buttons];
                          next[i] = { ...b, payload: `${WORKFLOW_PAYLOAD_PREFIX}${id}` };
                          set("buttons", next);
                        }}
                      >
                        <SelectTrigger aria-label={t("automations.fields.workflow")}>
                          <SelectValue placeholder={t("automations.fields.workflowPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {(workflows.data ?? []).length === 0 ? (
                            <div className="px-2 py-3 text-xs text-muted-foreground">
                              {workflows.isLoading
                                ? t("automations.fields.workflowsLoading")
                                : t("automations.fields.noWorkflows")}
                            </div>
                          ) : (
                            (workflows.data ?? []).map((w) => (
                              <SelectItem key={w.id} value={w.id}>
                                {w.name}
                                {w.status && w.status !== "active" ? ` (${w.status})` : ""}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        aria-label={t("automations.fields.buttonPayload")}
                        value={b.payload ?? ""}
                        onChange={(e) => {
                          const next = [...draft.buttons];
                          next[i] = { ...b, payload: e.target.value };
                          set("buttons", next);
                        }}
                      />
                    )}
                  </div>
                )}
                {b.type === "phone" && (
                  <Input
                    aria-label={t("automations.fields.buttonPhone")}
                    placeholder="+14155551234"
                    value={b.phone ?? ""}
                    onChange={(e) => {
                      const next = [...draft.buttons];
                      next[i] = { ...b, phone: e.target.value };
                      set("buttons", next);
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {isInstagram && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="ca-unlock">{t("automations.fields.tapToUnlock")}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t("automations.fields.tapToUnlockHint")}
                  </p>
                </div>
                <Switch
                  id="ca-unlock"
                  checked={draft.tapToUnlock}
                  onCheckedChange={(v) => set("tapToUnlock", v)}
                />
              </div>
              {draft.tapToUnlock && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="ca-gate-msg">{t("automations.fields.gateMessage")}</Label>
                    <Textarea
                      id="ca-gate-msg"
                      rows={2}
                      maxLength={640}
                      value={draft.gateMessage}
                      placeholder={t("automations.fields.gateMessagePlaceholder")}
                      onChange={(e) => set("gateMessage", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ca-gate-btn">{t("automations.fields.gateButton")}</Label>
                    <Input
                      id="ca-gate-btn"
                      maxLength={20}
                      value={draft.gateButtonLabel}
                      placeholder={t("automations.fields.gateButtonPlaceholder")}
                      onChange={(e) => set("gateButtonLabel", e.target.value)}
                    />
                  </div>
                </>
              )}
              {(draft.tapToUnlock || draft.buttons.length > 0) && (
                <p
                  role="note"
                  className="flex gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                >
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  {t("automations.fields.nonFollowerButtonsWarning")}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="ca-tracking">
              {t("automations.fields.linkTracking")}
            </Label>
            <Switch
              id="ca-tracking"
              checked={draft.linkTracking}
              onCheckedChange={(v) => set("linkTracking", v)}
            />
          </div>

          {draft.linkTracking && (
            <div className="space-y-1.5">
              <Label htmlFor="ca-tag">{t("automations.fields.clickTag")}</Label>
              <Input
                id="ca-tag"
                value={draft.clickTag}
                maxLength={50}
                onChange={(e) => set("clickTag", e.target.value)}
              />
            </div>
          )}
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("automations.actions.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving
              ? t("automations.actions.saving")
              : isEdit
                ? t("automations.actions.save")
                : t("automations.actions.create")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
